/**
 * A marker saying "this host left a half-built link to go and sort their calendar out".
 *
 * `draft.ts` already keeps the half-built link itself. What it cannot keep is where the host
 * was: the builders are not routes, they are local state inside `BookingPagesPage`, so any
 * navigation resets that page to the All-bookings list in ad-hoc mode. A rolling-link builder
 * came back to the ad-hoc one.
 *
 * `sessionStorage` rather than router state or a query param, because connecting a calendar
 * leaves the SPA entirely: the host is redirected out to Google or Microsoft and lands back on
 * a fresh page load at /profile. Router state and the history stack are gone by then, and the
 * OAuth callback redirects to a fixed URL that cannot carry a parameter of ours. Only the tab
 * survives, which is exactly what sessionStorage is scoped to.
 */
/** The offerings that have a builder to come back to. All three do. */
export type BuilderOffering = "adhoc" | "rollingLink" | "poll";

const OFFERINGS: readonly BuilderOffering[] = ["adhoc", "rollingLink", "poll"];

export type BuilderReturn = {
  /** Which builder to reopen. */
  offering: BuilderOffering;
  /** Epoch millis, so a marker left behind by an abandoned trip eventually stops firing. */
  savedAt: number;
};

const RETURN_KEY = "scheda.bookings.builder.return.v1";

/**
 * How long a marker stays good for. Long enough to sign in to a calendar provider, deal with a
 * consent screen and possibly upgrade a plan on the way; short enough that a host who wandered
 * off is not dragged back into a builder they had forgotten about.
 */
const RETURN_TTL_MS = 30 * 60 * 1000;

/** Remember to come back to this builder. Call it before navigating away. */
export function markBuilderReturn(offering: BuilderOffering): void {
  try {
    const mark: BuilderReturn = { offering, savedAt: Date.now() };
    window.sessionStorage.setItem(RETURN_KEY, JSON.stringify(mark));
  } catch {
    /* private mode / quota — the crumb just won't appear */
  }
}

/** The pending marker, if there is a live one. Does not consume it. */
export function peekBuilderReturn(): BuilderReturn | null {
  try {
    const raw = window.sessionStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const mark = JSON.parse(raw) as Partial<BuilderReturn>;
    if (!OFFERINGS.includes(mark.offering as BuilderOffering)) return null;
    if (typeof mark.savedAt !== "number" || Date.now() - mark.savedAt > RETURN_TTL_MS) {
      clearBuilderReturn();
      return null;
    }
    return mark as BuilderReturn;
  } catch {
    return null;
  }
}

/** The pending marker, cleared as it is read, so it reopens the builder exactly once. */
export function takeBuilderReturn(): BuilderReturn | null {
  const mark = peekBuilderReturn();
  clearBuilderReturn();
  return mark;
}

export function clearBuilderReturn(): void {
  try {
    window.sessionStorage.removeItem(RETURN_KEY);
  } catch {
    /* nothing to do */
  }
}
